package org.maplibre.compose.layers

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import kotlinx.serialization.json.JsonPrimitive
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.ast.TextUnitContextExpression
import org.maplibre.compose.expressions.ast.UnitConversion
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.textOffset
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.ColorValue
import org.maplibre.compose.expressions.value.DpOffsetValue
import org.maplibre.compose.expressions.value.DpPaddingValue
import org.maplibre.compose.expressions.value.DpValue
import org.maplibre.compose.expressions.value.FloatValue
import org.maplibre.compose.expressions.value.FormattedValue
import org.maplibre.compose.expressions.value.IconPitchAlignment
import org.maplibre.compose.expressions.value.IconRotationAlignment
import org.maplibre.compose.expressions.value.IconTextFit
import org.maplibre.compose.expressions.value.ImageValue
import org.maplibre.compose.expressions.value.ListValue
import org.maplibre.compose.expressions.value.StringValue
import org.maplibre.compose.expressions.value.SymbolAnchor
import org.maplibre.compose.expressions.value.SymbolHeightAnchor
import org.maplibre.compose.expressions.value.SymbolOverlap
import org.maplibre.compose.expressions.value.SymbolPlacement
import org.maplibre.compose.expressions.value.SymbolZOrder
import org.maplibre.compose.expressions.value.TextJustify
import org.maplibre.compose.expressions.value.TextPitchAlignment
import org.maplibre.compose.expressions.value.TextRotationAlignment
import org.maplibre.compose.expressions.value.TextTransform
import org.maplibre.compose.expressions.value.TextUnitOffsetValue
import org.maplibre.compose.expressions.value.TextUnitValue
import org.maplibre.compose.expressions.value.TextVariableAnchorOffsetValue
import org.maplibre.compose.expressions.value.TextWritingMode
import org.maplibre.compose.expressions.value.TranslateAnchor
import org.maplibre.compose.sources.VectorSource
import org.maplibre.compose.style.TransitionOptions
import org.maplibre.compose.style.styleFontScale
import org.maplibre.compose.util.DpPadding
import org.maplibre.compose.util.MaplibreComposable

private const val AssumedSp = 16f // MapLibre's default text size

@Composable
private fun rememberDpContext() =
  LayerExpressionContext(
    emScale = UnitConversion(const(AssumedSp), styleFontScale()),
    spScale = styleFontScale(),
  )

@Composable
private fun rememberEmContext(textSize: Expression<TextUnitValue>): LayerExpressionContext {
  val textSizeSp =
    remember(textSize) {
      TextUnitContextExpression(textSize, emScale = const(AssumedSp), spScale = const(1f))
    }
  val spScale = remember(textSizeSp) { UnitConversion(const(1f), textSizeSp.cast(), divide = true) }
  return LayerExpressionContext(emScale = const(1f), spScale = spScale)
}

/**
 * A symbol layer draws data from the [sourceLayer] in the given [source] as icons and/or text
 * labels.
 *
 * @param id Unique layer name.
 * @param source Vector data source for this layer.
 * @param sourceLayer Layer to use from the given vector tile [source].
 * @param minZoom The minimum zoom level for the layer. At zoom levels less than this, the layer
 *   will be hidden. A value in the range of `[0..24]`.
 * @param maxZoom The maximum zoom level for the layer. At zoom levels equal to or greater than
 *   this, the layer will be hidden. A value in the range of `[0..24]`.
 * @param filter An expression specifying conditions on source features. Only features that match
 *   the filter are displayed. Zoom expressions in filters are only evaluated at integer zoom
 *   levels. The expression may use feature properties. The
 *   [feature state][org.maplibre.compose.expressions.dsl.Feature.state] expression is not
 *   supported.
 * @param visible Whether the layer should be displayed.
 * @param sortKey Sorts features within this layer in ascending order based on this value. Features
 *   with a higher sort key will appear above features with a lower sort key. The expression may use
 *   feature properties.
 * @param placement Symbol placement relative to its geometry.
 * @param spacing Distance between two symbol anchors.
 *
 *   Only applicable when [placement] is [SymbolPlacement.Line].
 *
 * @param avoidEdges If true, the symbols will not cross tile edges to avoid mutual collisions.
 *   Recommended in layers that don't have enough padding in the vector tile to prevent collisions,
 *   or if it is a point symbol layer placed after a line symbol layer.
 * @param zOrder Determines whether overlapping symbols in the same layer are rendered in the order
 *   that they appear in the data source or by their y-position relative to the viewport. To control
 *   the order and prioritization of symbols otherwise, use [sortKey].
 * @param heightOffset Height in meters above [heightAnchor] at which the symbol is placed. Only
 *   applicable when [placement] is [SymbolPlacement.Point]. The expression may use feature
 *   properties.
 *
 *   Not yet supported on native
 *   ([maplibre-native#1929](https://github.com/maplibre/maplibre-native/issues/1929)).
 *
 * @param heightAnchor What [heightOffset] is measured from. [SymbolHeightAnchor.Ground] measures
 *   from the terrain surface below the symbol, or from zero when terrain is off.
 *   [SymbolHeightAnchor.Absolute] measures from sea level and ignores terrain.
 *
 *   Not yet supported on native
 *   ([maplibre-native#1929](https://github.com/maplibre/maplibre-native/issues/1929)).
 *
 * @param iconImage Image to use for drawing an image background. The expression may use feature
 *   properties.
 * @param iconOpacity The opacity at which the icon will be drawn. A value in the range `[0..1]`.
 *   The expression may use feature properties and feature state.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconOpacityTransition Timing for changes to [iconOpacity]. Null uses the style's global
 *   transition.
 * @param iconColor The color of the icon. This can only be used with SDF icons. The expression may
 *   use feature properties and feature state.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconColorTransition Timing for changes to [iconColor]. Null uses the style's global
 *   transition.
 * @param iconHaloColor The color of the icon's halo. Icon halos can only be used with SDF icons.
 *   The expression may use feature properties and feature state.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconHaloColorTransition Timing for changes to [iconHaloColor]. Null uses the style's
 *   global transition.
 * @param iconHaloWidth Distance of halo to the icon outline. The unit is in dp only for SDF sprites
 *   that were created with a blur radius of 8, multiplied by the display density. I.e., the radius
 *   needs to be 16 for @2x sprites, etc. The expression may use feature properties and feature
 *   state.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconHaloWidthTransition Timing for changes to [iconHaloWidth]. Null uses the style's
 *   global transition.
 * @param iconHaloBlur Fade out the halo towards the outside. The expression may use feature
 *   properties and feature state.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconHaloBlurTransition Timing for changes to [iconHaloBlur]. Null uses the style's global
 *   transition.
 * @param iconSize Scales the original size of the icon by the provided factor. The new pixel size
 *   of the image will be the original pixel size multiplied by [iconSize]. 1 is the original size;
 *   3 triples the size of the image. The expression may use feature properties.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconRotationAlignment In combination with [placement], determines the rotation behavior of
 *   icons.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconPitchAlignment Orientation of icon when map is pitched.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconTextFit Scales the icon to fit around the associated text.
 *
 *   Ignored if not both [iconImage] and [textField] are specified.
 *
 * @param iconTextFitPadding Size of the additional area added to dimensions determined by
 *   [iconTextFit]. A negative value shrinks that side.
 *
 *   Only applicable when if [iconTextFit] is not [IconTextFit.None]. Ignored if not both
 *   [iconImage] and [textField] are specified.
 *
 * @param iconKeepUpright If true, the icon may be flipped to prevent it from being rendered
 *   upside-down.
 *
 *   Only applicable if [iconRotationAlignment] is [IconRotationAlignment.Map] and [placement] is
 *   either [SymbolPlacement.Line] or [SymbolPlacement.LineCenter].
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconRotate Rotates the icon clockwise by the number in degrees. The expression may use
 *   feature properties.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconAnchor Part of the icon placed closest to the anchor. The expression may use feature
 *   properties.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconOffset Offset distance of icon from its anchor. Positive values indicate right and
 *   down, while negative values indicate left and up. Each component is multiplied by the value of
 *   [iconSize] to obtain the final offset in dp. When combined with [iconRotate] the offset will be
 *   as if the rotated direction was up. The expression may use feature properties.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconPadding Size of additional area round the icon bounding box used for detecting symbol
 *   collisions. A negative value shrinks that side. The expression may use feature properties.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconAllowOverlap If true, the icon will be visible even if it collides with other
 *   previously drawn symbols.
 *
 *   Ignored if [iconImage] is not specified, overridden by [iconOverlap], if specified.
 *
 * @param iconOverlap Controls whether to show an icon when it overlaps other symbols on the map.
 *
 *   Ignored if [iconImage] is not specified.
 *
 *   Not yet supported on native
 *   ([maplibre-native#251](https://github.com/maplibre/maplibre-native/issues/251)).
 *
 * @param iconIgnorePlacement If true, other symbols can be visible even if they collide with the
 *   icon.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconOptional If true, text will display without their corresponding icons when the icon
 *   collides with other symbols and the text does not.
 *
 *   Ignored if not both [iconImage] and [textField] are specified.
 *
 * @param iconTranslate The geometry's offset relative to the [iconTranslateAnchor]. Negative
 *   numbers indicate left and up, respectively.
 *
 *   Ignored if [iconImage] is not specified.
 *
 * @param iconTranslateTransition Timing for changes to [iconTranslate]. Null uses the style's
 *   global transition.
 * @param iconTranslateAnchor Frame of reference for offsetting geometry.
 *
 *   Ignored if [iconTranslate] is not set.
 *
 * @param textField Value to use for a text label. Use e.g. `format(const("My label"))` to display
 *   the plain string "My label". The expression may use feature properties.
 *
 *   The text can also be formatted, employing different colors, fonts, etc., see
 *   [format][org.maplibre.compose.expressions.dsl.format].
 *
 * @param textOpacity The opacity at which the text will be drawn. The expression may use feature
 *   properties and feature state.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textOpacityTransition Timing for changes to [textOpacity]. Null uses the style's global
 *   transition.
 * @param textColor The color with which the text will be drawn. The expression may use feature
 *   properties and feature state.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textColorTransition Timing for changes to [textColor]. Null uses the style's global
 *   transition.
 * @param textHaloColor The color of the text's halo, which helps it stand out from backgrounds. The
 *   expression may use feature properties and feature state.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textHaloColorTransition Timing for changes to [textHaloColor]. Null uses the style's
 *   global transition.
 * @param textHaloWidth Distance of halo to the font outline. Max text halo width is 1/4 of the
 *   font-size. The expression may use feature properties and feature state.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textHaloWidthTransition Timing for changes to [textHaloWidth]. Null uses the style's
 *   global transition.
 * @param textHaloBlur The halo's fadeout distance towards the outside. The expression may use
 *   feature properties and feature state.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textHaloBlurTransition Timing for changes to [textHaloBlur]. Null uses the style's global
 *   transition.
 * @param textFont Font stack to use for displaying text. The expression may use feature properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textSize Font size in SP or EM relative to 16sp. The expression may use feature
 *   properties.
 *
 *   Ignored if [textField] is not specified.
 *
 *   **Important:** If [textSize] depends on [zoom][org.maplibre.compose.expressions.dsl.zoom],
 *   define the other text-unit properties ([textLetterSpacing], [textOffset], and so on) in EM
 *   units, not SP or DP. SP and DP values depend on the text size, and MapLibre accepts
 *   [zoom][org.maplibre.compose.expressions.dsl.zoom] only as the input of a top-level
 *   [step][org.maplibre.compose.expressions.dsl.step] or
 *   [interpolate][org.maplibre.compose.expressions.dsl.interpolate].
 *
 * @param textTransform Specifies how to capitalize text. The expression may use feature properties.
 * @param textLetterSpacing Text tracking amount. The expression may use feature properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textRotationAlignment In combination with [placement], determines the rotation behavior of
 *   the individual glyphs forming the text.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textPitchAlignment Orientation of text when map is pitched.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textMaxAngle Maximum angle change in degrees between adjacent characters.
 *
 *   Only applicable if [placement] is [SymbolPlacement.Line] or [SymbolPlacement.LineCenter]
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textMaxWidth The maximum line width for text wrapping. The expression may use feature
 *   properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textLineHeight Text leading value for multi-line text.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textJustify Text justification options. The expression may use feature properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textWritingMode The property allows control over a symbol's orientation. Note that the
 *   property values act as a hint, so that a symbol whose language doesn’t support the provided
 *   orientation will be laid out in its natural orientation. Example: English point symbol will be
 *   rendered horizontally even if array value contains single 'vertical' enum value. The order of
 *   elements in an array define priority order for the placement of an orientation variant.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textKeepUpright If true, the text may be flipped vertically to prevent it from being
 *   rendered upside-down.
 *
 *   Only applicable if [textRotationAlignment] is [TextRotationAlignment.Map] and [placement] is
 *   [SymbolPlacement.Line] or [SymbolPlacement.LineCenter]
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textRotate Rotates the text clockwise. Unit in degrees. The expression may use feature
 *   properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textAnchor Part of the text placed closest to the anchor. The expression may use feature
 *   properties.
 *
 *   Overridden by [textVariableAnchorOffset].
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textOffset Offset distance of text from its anchor in ems. Positive values indicate right
 *   and down, while negative values indicate left and up. If used with [textVariableAnchor], input
 *   values will be taken as absolute values. Offsets along the x- and y-axis will be applied
 *   automatically based on the anchor position. The expression may use feature properties.
 *
 *   Use [org.maplibre.compose.expressions.dsl.textOffset] with DP arguments for a fixed distance,
 *   or SP/EM arguments for a distance that scales with text. See [textSize] for the zoom
 *   restriction.
 *
 *   Overridden by [textRadialOffset].
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textVariableAnchor To increase the chance of placing high-priority labels on the map, you
 *   can provide an array of [textAnchor] locations: the renderer will attempt to place the label at
 *   each location, in order, before moving onto the next label. Use [textJustify] =
 *   [TextJustify.Auto] to choose justification based on anchor position. To apply an offset, use
 *   the [textRadialOffset] or the two-dimensional [textOffset].
 *
 *   Only applicable if [placement] is [SymbolPlacement.Point].
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textRadialOffset Radial offset of text, in the direction of the symbol's anchor. Useful in
 *   combination with [textVariableAnchor], which defaults to using the two-dimensional [textOffset]
 *   if present. The expression may use feature properties.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textVariableAnchorOffset To increase the chance of placing high-priority labels on the
 *   map, you can provide an array of [SymbolAnchor] locations, each paired with an offset value.
 *   The renderer will attempt to place the label at each location, in order, before moving on to
 *   the next location+offset. Use [textJustify] = [TextJustify.Auto] to choose justification based
 *   on anchor position. The expression may use feature properties.
 *
 * Each anchor location has a [textOffset] expression in SP, EM, or DP. Positive offset values
 * indicate right and down, while negative values indicate left and up. Anchor locations may repeat,
 * which lets the renderer try multiple offsets for the same anchor.
 *
 * When present, this property takes precedence over [textAnchor], [textVariableAnchor],
 * [textOffset] and [textRadialOffset].
 *
 * Example:
 * ```kt
 * textVariableAnchorOffset(
 *   SymbolAnchor.Top to textOffset(0.em, 4.em),
 *   SymbolAnchor.Left to textOffset(3.em, 0.em),
 *   SymbolAnchor.Bottom to textOffset(1.em, 1.em),
 * )
 * ```
 *
 * When the renderer chooses the top anchor, [0, 4] will be used for [textOffset]; the text will be
 * shifted down by 4 ems. When the renderer chooses the left anchor, [3, 0] will be used for
 * [textOffset]; the text will be shifted right by 3 ems.
 *
 * Ignored if [textField] is not specified.
 *
 * @param textPadding Size of the additional area in dp around the text bounding box used for
 *   detecting symbol collisions.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textAllowOverlap If true, the text will be visible even if it collides with other
 *   previously drawn symbols.
 *
 *   Overridden by [textOverlap].
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textOverlap Controls whether to show an icon/text when it overlaps other symbols on the
 *   map. See [SymbolOverlap][org.maplibre.compose.expressions.value.SymbolOverlap]. Overrides
 *   [textAllowOverlap].
 *
 *   Ignored if [textField] is not specified.
 *
 *   Not yet supported on native
 *   ([maplibre-native#251](https://github.com/maplibre/maplibre-native/issues/251)).
 *
 * @param textIgnorePlacement If true, other symbols can be visible even if they collide with the
 *   text.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textOptional If true, icons will display without their corresponding text when the text
 *   collides with other symbols and the icon does not.
 *
 *   Ignored if not both [textField] and [iconImage] are specified.
 *
 * @param textTranslate Distance that the text's anchor is moved from its original placement in dp.
 *   Positive values indicate right and down, while negative values indicate left and up.
 *
 *   Ignored if [textField] is not specified.
 *
 * @param textTranslateTransition Timing for changes to [textTranslate]. Null uses the style's
 *   global transition.
 * @param textTranslateAnchor Controls the frame of reference for [textTranslate].
 *
 *   Ignored if [textField] is not specified.
 *
 * @param onClick Function to call when any feature in this layer has been clicked.
 * @param onLongClick Called for a touch long press or secondary mouse click on this layer.
 * @param onDoubleClick Called for a double tap or double click on this layer.
 * @param hitPadding Expands tap queries to a square of this radius in dp; zero uses a point.
 */
@Composable
@MaplibreComposable
public fun SymbolLayer(
  id: String,
  source: VectorSource,
  sourceLayer: String = "",
  minZoom: Float = 0.0f,
  maxZoom: Float = 24.0f,
  filter: Expression<BooleanValue>? = null,
  visible: Boolean = true,
  sortKey: Expression<FloatValue>? = null,
  placement: Expression<SymbolPlacement> = const(SymbolPlacement.Point),
  spacing: Expression<DpValue> = const(250.dp),
  avoidEdges: Expression<BooleanValue> = const(false),
  zOrder: Expression<SymbolZOrder> = const(SymbolZOrder.Auto),
  heightOffset: Expression<FloatValue>? = null,
  heightAnchor: Expression<SymbolHeightAnchor>? = null,

  // icon image
  iconImage: Expression<ImageValue?>? = null,

  // icon colors
  iconOpacity: Expression<FloatValue> = const(1f),
  iconOpacityTransition: TransitionOptions? = null,
  iconColor: Expression<ColorValue> = const(Color.Black),
  iconColorTransition: TransitionOptions? = null,
  iconHaloColor: Expression<ColorValue> = const(Color.Transparent),
  iconHaloColorTransition: TransitionOptions? = null,
  iconHaloWidth: Expression<DpValue> = const(0.dp),
  iconHaloWidthTransition: TransitionOptions? = null,
  iconHaloBlur: Expression<DpValue> = const(0.dp),
  iconHaloBlurTransition: TransitionOptions? = null,

  // icon layout
  iconSize: Expression<FloatValue> = const(1f),
  iconRotationAlignment: Expression<IconRotationAlignment> = const(IconRotationAlignment.Auto),
  iconPitchAlignment: Expression<IconPitchAlignment> = const(IconPitchAlignment.Auto),
  iconTextFit: Expression<IconTextFit> = const(IconTextFit.None),
  iconTextFitPadding: Expression<DpPaddingValue> = const(DpPadding.Zero),
  iconKeepUpright: Expression<BooleanValue> = const(false),
  iconRotate: Expression<FloatValue> = const(0f),

  // icon anchoring
  iconAnchor: Expression<SymbolAnchor> = const(SymbolAnchor.Center),
  iconOffset: Expression<DpOffsetValue> = const(DpOffset.Zero),

  // icon collision
  iconPadding: Expression<DpPaddingValue> = const(DpPadding(2.dp, 2.dp, 2.dp, 2.dp)),
  iconAllowOverlap: Expression<BooleanValue> = const(false),
  iconOverlap: Expression<StringValue>? = null,
  iconIgnorePlacement: Expression<BooleanValue> = const(false),
  iconOptional: Expression<BooleanValue> = const(false),

  // icon translate
  iconTranslate: Expression<DpOffsetValue> = const(DpOffset.Zero),
  iconTranslateTransition: TransitionOptions? = null,
  iconTranslateAnchor: Expression<TranslateAnchor> = const(TranslateAnchor.Map),

  // text content
  textField: Expression<FormattedValue?> = const("").cast(),

  // text glyph colors
  textOpacity: Expression<FloatValue> = const(1f),
  textOpacityTransition: TransitionOptions? = null,
  textColor: Expression<ColorValue> = const(Color.Black),
  textColorTransition: TransitionOptions? = null,
  textHaloColor: Expression<ColorValue> = const(Color.Transparent),
  textHaloColorTransition: TransitionOptions? = null,
  textHaloWidth: Expression<DpValue> = const(0.dp),
  textHaloWidthTransition: TransitionOptions? = null,
  textHaloBlur: Expression<DpValue> = const(0.dp),
  textHaloBlurTransition: TransitionOptions? = null,

  // text glyph properties
  textFont: Expression<ListValue<StringValue>> = LayerDefaults.FontNames,
  textSize: Expression<TextUnitValue> = const(1.em),
  textTransform: Expression<TextTransform> = const(TextTransform.None),
  textLetterSpacing: Expression<TextUnitValue> = const(0f.em),
  textRotationAlignment: Expression<TextRotationAlignment> = const(TextRotationAlignment.Auto),
  textPitchAlignment: Expression<TextPitchAlignment> = const(TextPitchAlignment.Auto),
  textMaxAngle: Expression<FloatValue> = const(45f),

  // text paragraph layout
  textMaxWidth: Expression<TextUnitValue> = const(10f.em),
  textLineHeight: Expression<TextUnitValue> = const(1.2f.em),
  textJustify: Expression<TextJustify> = const(TextJustify.Center),
  textWritingMode: Expression<ListValue<TextWritingMode>>? = null,
  textKeepUpright: Expression<BooleanValue> = const(true),
  textRotate: Expression<FloatValue> = const(0f),

  // text anchoring
  textAnchor: Expression<SymbolAnchor> = const(SymbolAnchor.Center),
  textOffset: Expression<TextUnitOffsetValue> = textOffset(0f.em, 0f.em),
  textVariableAnchor: Expression<ListValue<SymbolAnchor>>? = null,
  textRadialOffset: Expression<TextUnitValue> = const(0f.em),
  textVariableAnchorOffset: Expression<TextVariableAnchorOffsetValue>? = null,

  // text collision
  textPadding: Expression<DpValue> = const(2.dp),
  textAllowOverlap: Expression<BooleanValue> = const(false),
  textOverlap: Expression<SymbolOverlap>? = null,
  textIgnorePlacement: Expression<BooleanValue> = const(false),
  textOptional: Expression<BooleanValue> = const(false),

  // text translate
  textTranslate: Expression<DpOffsetValue> = const(DpOffset.Zero),
  textTranslateTransition: TransitionOptions? = null,
  textTranslateAnchor: Expression<TranslateAnchor> = const(TranslateAnchor.Map),
  onClick: FeaturesClickHandler? = null,
  onLongClick: FeaturesClickHandler? = null,
  onDoubleClick: FeaturesClickHandler? = null,
  hitPadding: Dp = 0.dp,
) {
  // Scaling code will need changes after https://github.com/maplibre/maplibre-native/issues/3057.
  val contextWithDpTextSize = rememberDpContext()
  val contextWithEmTextSize = rememberEmContext(textSize)

  Layer(
    id = id,
    source = source,
    type = "symbol",
    filterUnsupportedProperties = true,
    onClick = onClick,
    onLongClick = onLongClick,
    onDoubleClick = onDoubleClick,
    hitPadding = hitPadding,
  ) {
    root("source-layer", JsonPrimitive(sourceLayer))
    root("minzoom", JsonPrimitive(minZoom))
    root("maxzoom", JsonPrimitive(maxZoom))
    root("filter", filter)
    layout("visibility", JsonPrimitive(if (visible) "visible" else "none"))
    layout("symbol-placement", placement)
    layout("symbol-spacing", spacing)
    layout("symbol-avoid-edges", avoidEdges)
    layout("symbol-sort-key", sortKey)
    layout("symbol-z-order", zOrder)
    layout("symbol-height-offset", heightOffset)
    layout("symbol-height-anchor", heightAnchor)

    layout("icon-allow-overlap", iconAllowOverlap)
    layout("icon-overlap", iconOverlap)
    layout("icon-ignore-placement", iconIgnorePlacement)
    layout("icon-optional", iconOptional)
    layout("icon-rotation-alignment", iconRotationAlignment)
    layout("icon-size", iconSize)
    layout("icon-text-fit", iconTextFit)
    layout("icon-text-fit-padding", iconTextFitPadding)
    layout("icon-image", iconImage)
    layout("icon-rotate", iconRotate)
    layout("icon-padding", iconPadding)
    layout("icon-keep-upright", iconKeepUpright)
    layout("icon-offset", iconOffset)
    layout("icon-anchor", iconAnchor)
    layout("icon-pitch-alignment", iconPitchAlignment)
    paint("icon-opacity", iconOpacity)
    paintTransition("icon-opacity", iconOpacityTransition)
    paint("icon-color", iconColor)
    paintTransition("icon-color", iconColorTransition)
    paint("icon-halo-color", iconHaloColor)
    paintTransition("icon-halo-color", iconHaloColorTransition)
    paint("icon-halo-width", iconHaloWidth)
    paintTransition("icon-halo-width", iconHaloWidthTransition)
    paint("icon-halo-blur", iconHaloBlur)
    paintTransition("icon-halo-blur", iconHaloBlurTransition)
    paint("icon-translate", iconTranslate)
    paintTransition("icon-translate", iconTranslateTransition)
    paint("icon-translate-anchor", iconTranslateAnchor)

    layout("text-pitch-alignment", textPitchAlignment)
    layout("text-rotation-alignment", textRotationAlignment)
    layout("text-field", textField)
    layout("text-font", textFont)
    layout("text-size", textSize, units = contextWithDpTextSize)
    layout("text-max-width", textMaxWidth, units = contextWithEmTextSize)
    layout("text-line-height", textLineHeight, units = contextWithEmTextSize)
    layout("text-letter-spacing", textLetterSpacing, units = contextWithEmTextSize)
    layout("text-justify", textJustify)
    layout("text-radial-offset", textRadialOffset, units = contextWithEmTextSize)
    layout("text-variable-anchor", textVariableAnchor)
    layout(
      "text-variable-anchor-offset",
      textVariableAnchorOffset,
      units = contextWithEmTextSize,
    )
    layout("text-anchor", textAnchor)
    layout("text-max-angle", textMaxAngle)
    layout("text-writing-mode", textWritingMode)
    layout("text-rotate", textRotate)
    layout("text-padding", textPadding)
    layout("text-keep-upright", textKeepUpright)
    layout("text-transform", textTransform)
    layout("text-offset", textOffset, units = contextWithEmTextSize)
    layout("text-allow-overlap", textAllowOverlap)
    layout("text-overlap", textOverlap)
    layout("text-ignore-placement", textIgnorePlacement)
    layout("text-optional", textOptional)
    paint("text-opacity", textOpacity)
    paintTransition("text-opacity", textOpacityTransition)
    paint("text-color", textColor)
    paintTransition("text-color", textColorTransition)
    paint("text-halo-color", textHaloColor)
    paintTransition("text-halo-color", textHaloColorTransition)
    paint("text-halo-width", textHaloWidth)
    paintTransition("text-halo-width", textHaloWidthTransition)
    paint("text-halo-blur", textHaloBlur)
    paintTransition("text-halo-blur", textHaloBlurTransition)
    paint("text-translate", textTranslate)
    paintTransition("text-translate", textTranslateTransition)
    paint("text-translate-anchor", textTranslateAnchor)
  }
}
